import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconPhoto, IconTrash, IconLoader2, IconSend, IconMessage2 } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import printTemplateFeedbackService from '../../../services/printTemplateFeedbackService';

const MAX_IMAGE_BYTES = 1024 * 1024; // 1 MB — the server enforces the same
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Feedback form: a message AND a screenshot — both required — with an
 * are-you-sure before it is sent.
 *
 * Built on the shared Dialog so it layers, locks scroll and closes on
 * Escape/outside-click the same way every other modal in the app does. It also
 * has to sit BELOW ConfirmDialog (z-200) — otherwise the confirmation opens
 * behind this and the submit button looks dead.
 *
 * The 1 MB cap is checked here for a useful error, and again on the server —
 * this one is a courtesy, that one is the rule.
 */
export default function PrintFeedbackModal({ open, onClose }) {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const [image, setImage] = useState(null);        // File | null
    const [previewUrl, setPreviewUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    // Set while the "you can't dismiss me that way" nudge plays.
    const [nudging, setNudging] = useState(false);
    const [dragging, setDragging] = useState(false);
    const fileRef = useRef(null);
    // dragenter/dragleave also fire when the pointer crosses CHILD elements, so
    // a plain boolean flickers. Counting enters and leaves is what makes the
    // highlight stable while moving around inside the drop area.
    const dragDepth = useRef(0);

    /**
     * Clicking the backdrop or pressing Escape does NOT close this form — losing
     * a half-written report to a stray click is worse than an extra click to
     * leave. Instead the card gives a short squeeze so the dismissal is clearly
     * refused rather than silently ignored.
     *
     * Turned off and back on across a frame so repeated clicks restart the
     * animation instead of doing nothing while it is already running.
     */
    const nudge = () => {
        setNudging(false);
        requestAnimationFrame(() => setNudging(true));
    };

    /**
     * The confirmation is a separate dialog portalled to the body, so touching it
     * registers as an interaction OUTSIDE this one — without this guard the form
     * squirms in the background the whole time the confirmation is up. Same while
     * a submit is in flight: nothing the user does should be answered with a
     * refusal animation then.
     */
    const dismissable = !confirming && !submitting;

    // Object URLs are a manual resource — revoke the old one whenever the pick
    // changes, and on unmount, or the tab leaks a blob per image chosen.
    useEffect(() => {
        if (!image) { setPreviewUrl(null); return undefined; }
        const url = URL.createObjectURL(image);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    /**
     * Ctrl+V anywhere in the form takes an image off the clipboard — a
     * screenshot usually IS on the clipboard, and saving it to disk first just
     * to re-pick it is a pointless round trip.
     *
     * Only image items are claimed; pasting text still lands in the textarea as
     * normal, because we do not touch the event unless there is a file on it.
     */
    useEffect(() => {
        if (!open) return undefined;
        const onPaste = (e) => {
            const item = [...(e.clipboardData?.items || [])]
                .find((i) => i.kind === 'file' && i.type.startsWith('image/'));
            if (!item) return;
            const file = item.getAsFile();
            if (!file) return;
            e.preventDefault();
            pickImage(file);
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- pickImage is stable enough; re-binding per keystroke is not
    }, [open]);

    // Reset whenever it opens, so a previous draft never reappears.
    useEffect(() => {
        if (!open) return;
        setMessage('');
        setImage(null);
        setSubmitting(false);
        setConfirming(false);
        setDragging(false);
        dragDepth.current = 0;
    }, [open]);

    const pickImage = (file) => {
        if (!file) return;
        if (!ALLOWED_TYPES.includes(file.type)) {
            notifications.show({ title: t('printFeedback.error'), message: t('printFeedback.imageWrongType'), color: 'red' });
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            notifications.show({ title: t('printFeedback.error'), message: t('printFeedback.imageTooLarge'), color: 'red' });
            return;
        }
        setImage(file);
    };

    // A drop anywhere on the card counts — hunting for the exact box is a chore,
    // and there is only one thing here that can accept a file.
    const onDragEnter = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
    };
    const onDragOver = (e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();   // required, or the browser refuses the drop
    };
    const onDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        pickImage(e.dataTransfer.files[0]);
    };

    const askSubmit = () => {
        if (!message.trim()) {
            notifications.show({ title: t('printFeedback.error'), message: t('printFeedback.messageRequired'), color: 'orange' });
            return;
        }
        if (!image) {
            notifications.show({ title: t('printFeedback.error'), message: t('printFeedback.imageRequired'), color: 'orange' });
            return;
        }
        setConfirming(true);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            await printTemplateFeedbackService.submit({ message: message.trim(), image });
            notifications.show({ title: t('common.success'), message: t('printFeedback.sent'), color: 'green' });
            setConfirming(false);
            onClose();
        } catch (err) {
            notifications.show({
                title: t('printFeedback.error'),
                message: err.message || t('printFeedback.failed'),
                color: 'red',
            });
            setConfirming(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
                <DialogContent
                    className="max-w-lg p-0 overflow-hidden z-[150] font-hind"
                    overlayClassName="z-[149]"
                    aria-describedby={undefined}
                    onInteractOutside={(e) => { e.preventDefault(); if (dismissable) nudge(); }}
                    onEscapeKeyDown={(e) => { e.preventDefault(); if (dismissable) nudge(); }}
                >
                    <DialogTitle className="sr-only">{t('printFeedback.modalTitle')}</DialogTitle>

                    {/* The squeeze lives on an inner wrapper: DialogContent centres
                        itself with a translate, and animating transform there would
                        knock the card off-centre. */}
                    <style>{`
                        @keyframes skj-feedback-nudge {
                            0%, 100% { transform: scale(1); }
                            40%      { transform: scale(0.975); }
                            70%      { transform: scale(1.006); }
                        }
                    `}</style>
                    <div
                        onAnimationEnd={() => setNudging(false)}
                        style={nudging ? { animation: 'skj-feedback-nudge 220ms ease-in-out' } : undefined}
                        onDragEnter={onDragEnter}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >

                    {/* Header */}
                    <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b border-[#ECE9E3] dark:border-zinc-800">
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FCEFE7] dark:bg-orange-900/20 flex items-center justify-center">
                            <IconMessage2 size={20} className="text-[#E2702E]" />
                        </span>
                        <div className="min-w-0 pr-6">
                            <p className="text-[15px] font-bold text-[#1C1B19] dark:text-zinc-100">
                                {t('printFeedback.modalTitle')}
                            </p>
                            <p className="mt-0.5 text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                                {t('printFeedback.modalSubtitle')}
                            </p>
                        </div>
                    </div>

                    {/* Body — scrolls on short screens rather than pushing the footer off */}
                    <div className="px-5 py-4 flex flex-col gap-4 max-h-[60vh] overflow-y-auto">
                        <div>
                            <div className="flex items-baseline justify-between mb-1.5">
                                <label htmlFor="print-feedback-message" className="text-[12.5px] font-semibold text-[#1C1B19] dark:text-zinc-200">
                                    {t('printFeedback.messageLabel')} <span className="text-red-500">*</span>
                                </label>
                                <span className="text-[11px] text-[#9C988F] dark:text-zinc-500 tabular-nums">
                                    {message.length} / 5000
                                </span>
                            </div>
                            <textarea
                                id="print-feedback-message"
                                autoFocus
                                rows={5}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                maxLength={5000}
                                placeholder={t('printFeedback.messagePlaceholder')}
                                className="w-full rounded-[10px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2.5 text-[13.5px] text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-500 outline-none focus:border-[#E2702E] transition-colors resize-y min-h-[110px]"
                            />
                        </div>

                        <div>
                            <label className="block text-[12.5px] font-semibold text-[#1C1B19] dark:text-zinc-200 mb-1.5">
                                {t('printFeedback.imageLabel')} <span className="text-red-500">*</span>
                                {/* The aside stays lighter than the label so the
                                    field name still reads first. */}
                                <span className="ml-1 font-normal text-[11.5px] text-[#9C988F] dark:text-zinc-500">
                                    {t('printFeedback.imageLabelNote')}
                                </span>
                            </label>

                            <input
                                ref={fileRef}
                                type="file"
                                accept={ALLOWED_TYPES.join(',')}
                                className="hidden"
                                onChange={(e) => { pickImage(e.target.files?.[0]); e.target.value = ''; }}
                            />

                            {/* Keyed off `image`, NOT `previewUrl`: state updates render
                                before effects run, so on the frame after Remove the two
                                disagree — and reading image.size there blanked the page. */}
                            {image ? (
                                <div className={`rounded-[10px] border bg-white dark:bg-zinc-900 p-2.5 flex items-center gap-3 transition-colors ${
                                    dragging ? 'border-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/20' : 'border-[#E9E6E0] dark:border-zinc-700'
                                }`}>
                                    <div className="w-14 h-14 rounded-lg overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0">
                                        {previewUrl && <img src={previewUrl} alt="" className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[12.5px] font-medium text-[#1C1B19] dark:text-zinc-200 truncate">{image.name}</p>
                                        <p className="text-[11px] text-[#9C988F] dark:text-zinc-500">
                                            {(image.size / 1024).toFixed(0)} KB
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            className="h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                                        >
                                            {t('printFeedback.imageReplace')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setImage(null)}
                                            aria-label={t('printFeedback.imageRemove')}
                                            className="w-8 h-8 rounded-lg border border-red-200 dark:border-red-900/40 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
                                        >
                                            <IconTrash size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className={`w-full rounded-[10px] border border-dashed px-4 py-5 flex flex-col items-center gap-1 transition-colors cursor-pointer ${
                                        dragging
                                            ? 'border-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/20 text-[#E2702E]'
                                            : 'border-[#D9D5CD] dark:border-zinc-700 bg-[#FAF9F6] dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-400 hover:border-[#E2702E] hover:text-[#E2702E] hover:bg-[#FCEFE7] dark:hover:bg-orange-950/20'
                                    }`}
                                >
                                    <IconPhoto size={22} />
                                    <span className="text-[12.5px] font-semibold">{t('printFeedback.imagePick')}</span>
                                    <span className="text-[11px] text-[#9C988F] dark:text-zinc-500">{t('printFeedback.imageDropHint')}</span>
                                    <span className="text-[11px] text-[#9C988F] dark:text-zinc-500">{t('printFeedback.imageHint')}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Footer — split buttons, the same shape as ConfirmDialog's */}
                    <div className="flex border-t border-[#ECE9E3] dark:border-zinc-800">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 py-3 text-[13px] font-medium text-[#6E6A63] dark:text-zinc-400 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors border-r border-[#ECE9E3] dark:border-zinc-800 cursor-pointer disabled:opacity-50"
                        >
                            {t('printFeedback.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={askSubmit}
                            disabled={submitting || !message.trim() || !image}
                            className="flex-1 py-3 text-[13px] font-semibold text-[#E2702E] hover:bg-[#FCEFE7] dark:hover:bg-orange-900/20 transition-colors inline-flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                            {submitting ? <IconLoader2 size={15} className="animate-spin" /> : <IconSend size={15} />}
                            {t('printFeedback.submit')}
                        </button>
                    </div>
                    </div>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={confirming}
                onClose={() => setConfirming(false)}
                onConfirm={submit}
                loading={submitting}
                variant="primary"
                title={t('printFeedback.confirmTitle')}
                description={t('printFeedback.confirmDesc')}
                confirmLabel={t('printFeedback.submit')}
            />
        </>
    );
}
